import { createContext, useContext, useState } from "react";
import {
  AllCommentsProps,
  BusniessOfficeNote,
} from "../data-model/busniessOfficeNote";

interface BusniessOfficeNotesContextType {
  allCommentsForUI: AllCommentsProps[];
  globalBoValue: string;
  orderId?: string;
  authStatus?: string;
  manualAuthStatus?: string;
  rawData?: BusniessOfficeNote[];
  hasCptTableAnyError: boolean;
  shouldDisableMarkAsComplete: boolean;
  setAllCommentsForUI: (
    allCommentsForUI:
      | AllCommentsProps[]
      | ((prev: AllCommentsProps[]) => AllCommentsProps[]),
  ) => void;
  setGlobalBoValue: (globalBoValue: string) => void;
  setOrderId: (orderId: string) => void;
  setAuthStatus: (authStatus: string) => void;
  setManualAuthStatus: (manualAuthStatus: string) => void;
  setRawData: (rawData: BusniessOfficeNote[]) => void;
  setHasCptTableAnyError: (hasCptTableAnyError: boolean) => void;
  setShouldDisableMarkAsComplete: (
    shouldDisableMarkAsComplete: boolean,
  ) => void;
}
const BusniessOfficeNotesContext =
  createContext<BusniessOfficeNotesContextType>({
    allCommentsForUI: [],
    globalBoValue: "",
    setAllCommentsForUI: (
      allCommentsForUI:
        | AllCommentsProps[]
        | ((prev: AllCommentsProps[]) => AllCommentsProps[]),
    ) => {},
    setGlobalBoValue: () => {},
    setOrderId: () => {},
    orderId: "",
    authStatus: "",
    setAuthStatus: () => {},
    manualAuthStatus: "",
    setManualAuthStatus: () => {},
    setRawData: () => {},
    rawData: [],
    hasCptTableAnyError: false,
    setHasCptTableAnyError: () => {},
    shouldDisableMarkAsComplete: false,
    setShouldDisableMarkAsComplete: () => {},
  });

export const useBusniessOfficeNotesContext = () => {
  const context = useContext(BusniessOfficeNotesContext);
  if (!context) {
    throw new Error(
      "useBusniessOfficeNotesContext must be used within a BusniessOfficeNotesContextProvider",
    );
  }
  return context;
};

export const BusniessOfficeNotesContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [allCommentsForUI, setAllCommentsForUI] = useState<AllCommentsProps[]>(
    [],
  );
  const [globalBoValue, setGlobalBoValue] = useState<string>("");
  const [orderId, setOrderId] = useState<string>("");
  const [authStatus, setAuthStatus] = useState<string>("");
  const [manualAuthStatus, setManualAuthStatus] = useState<string>("");
  const [rawData, setRawData] = useState<BusniessOfficeNote[]>([]);
  const [hasCptTableAnyError, setHasCptTableAnyError] =
    useState<boolean>(false);
  const [shouldDisableMarkAsComplete, setShouldDisableMarkAsComplete] =
    useState<boolean>(false);
  return (
    <BusniessOfficeNotesContext.Provider
      value={{
        allCommentsForUI,
        setAllCommentsForUI: (
          allCommentsForUI:
            | AllCommentsProps[]
            | ((prev: AllCommentsProps[]) => AllCommentsProps[]),
        ) => {
          setAllCommentsForUI(allCommentsForUI);
        },
        globalBoValue,
        setGlobalBoValue: (globalBoValue: string) => {
          setGlobalBoValue(globalBoValue);
        },
        orderId,
        setOrderId: (orderId: string) => {
          setOrderId(orderId);
        },
        authStatus,
        setAuthStatus: (authStatus: string) => {
          setAuthStatus(authStatus);
        },
        manualAuthStatus,
        setManualAuthStatus: (manualAuthStatus: string) => {
          setManualAuthStatus(manualAuthStatus);
        },
        rawData,
        setRawData: (rawData: BusniessOfficeNote[]) => {
          setRawData(rawData);
        },
        hasCptTableAnyError,
        setHasCptTableAnyError: (hasCptTableAnyError: boolean) => {
          setHasCptTableAnyError(hasCptTableAnyError);
        },
        shouldDisableMarkAsComplete,
        setShouldDisableMarkAsComplete: (
          shouldDisableMarkAsComplete: boolean,
        ) => {
          setShouldDisableMarkAsComplete(shouldDisableMarkAsComplete);
        },
      }}
    >
      {children}
    </BusniessOfficeNotesContext.Provider>
  );
};
